// BaseAddModal.jsx

import React, { useEffect, useState } from 'react';

import { useProgress } from 'context/ProgressContext';
import { useUser } from 'context/UserContext';
import { useIsMobile } from 'hooks/useDeviceDetect';
import { createBase } from 'services/bases';
import { BASE_VISIBILITY } from 'utilities/baseVisibility';

import './BaseAddModal.css';

import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';
import { toast } from 'react-toastify';

/**
 * Modal for creating a base.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Sets open state.
 * @param {function} [onSave] - Called with the created base.
 * @param {boolean} [required=false] - Onboarding mode: no dismiss/cancel.
 *
 * @returns {ReactNode}
 */
const BaseAddModal = ({
  isOpen,
  setOpen,
  onSave,
  required = false,
}) => {
  const [title, setTitle] = useState('');

  const { showProgress, hideProgress } = useProgress();
  const { user } = useUser();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen) return undefined;
    setTitle('');
    return undefined;
  }, [isOpen]);

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    if (!user?.key) return;

    try {
      showProgress && showProgress('Creating knowledge base…');
      const created = await createBase(user.key, trimmedTitle, user, {
        visibility: BASE_VISIBILITY.PRIVATE,
      });
      if (!created?.key) return;

      onSave && onSave(created);
      if (!required) setOpen(false);
    } catch (error) {
      console.error('[BaseAddModal] create base error:', error);
      toast.error('Unable to create a new workspace right now. Please try again.');
    } finally {
      hideProgress && hideProgress();
    }
  };

  return (
    <Modal
      iconKey="workspaces"
      title={required ? 'Name your workspace' : 'Create Knowledge Base'}
      isOpen={isOpen}
      width={isMobile ? '100%' : 460}
      height="auto"
      enableClose={!required}
      onClose={() => {
        if (required) return;
        setOpen(false);
      }}
    >
      <ModalForm>
        <div className="base-add-modal-container">
          {required ? (
            <div className="base-add-modal-onboarding-copy">
              Create your first knowledge base to get started. You get a free trial with it.
            </div>
          ) : null}
          <ModalLabel label="Title" />
          <TextInput
            value={title}
            maxLength={100}
            autoFocus
            autoSelect
            showCount
            placeholder={required ? 'e.g. Acme HQ, Family Projects…' : 'Name this knowledge base...'}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        {!required ? (
          <Button
            label="Cancel"
            onClick={() => setOpen(false)}
          />
        ) : null}
        <Button
          label={required ? 'Create workspace' : 'Create'}
          disabled={!title.trim()}
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
};

BaseAddModal.displayName = 'BaseAddModal';

export default BaseAddModal;
